import { DropdownObj, ProjectCategoryOption } from "pages/Common/commonTypes"

/**
 * The project category that makes a solo logistics ticket a complaint-linked service dispatch.
 * Selecting it is what mounts the mandatory Complaint Reference ID field on Step 3; every other
 * category's Project Details step is unchanged. It is also the category the complaint drawer
 * pre-sets and locks, since the user is given no picker there.
 *
 * Identified by id first, then by name as a fallback.
 *
 * CAVEAT — the id is environment-specific: the category master is seeded per environment, so the
 * same category carries a different `category_id` between SIT and production. Id 10 was in this
 * list and turned out to be the wrong category, which is what the name fallback exists to survive.
 * Backend has been asked for a stable `category_code`; move both checks onto it when it lands.
 */
export const COMPLAINT_LINKED_CATEGORY_IDS = [11]

/** Fallback identity, used wherever the id above does not match this environment's master. */
export const COMPLAINT_LINKED_CATEGORY_NAME = "Service Material Movement"

const categoryIdOf = (category: DropdownObj | ProjectCategoryOption): number =>
    Number("category_id" in category ? category.category_id : category.id)

const categoryNameOf = (category: DropdownObj | ProjectCategoryOption): string =>
    "category_name" in category ? category.category_name : String(category.title ?? "")

/** Tolerates casing and stray whitespace in the master so a rename does not silently break matching. */
const isLinkedCategoryName = (name: string): boolean =>
    // Strips the "(Inactive)" suffix useProjectCategoryOptions appends to built dropdown options.
    name.replace(/\s*\(inactive\)\s*$/i, "").trim().toLowerCase() ===
    COMPLAINT_LINKED_CATEGORY_NAME.toLowerCase()

/**
 * Whether a category — as either a master row or a picked dropdown option — is the
 * complaint-linked one.
 *
 * Id first, name as the fallback, so the category still resolves in an environment that numbers
 * the master differently. The two must agree: this is the same check the drawer's pre-fill is
 * validated against, and without the name arm the drawer would pre-set the right category and then
 * hide the Complaint Reference ID field, which mounts behind this.
 */
export const isComplaintLinkedCategory = (
    category?: DropdownObj | ProjectCategoryOption | null
): boolean => {
    if (!category) return false
    return (
        COMPLAINT_LINKED_CATEGORY_IDS.includes(categoryIdOf(category)) ||
        isLinkedCategoryName(categoryNameOf(category))
    )
}

/**
 * Resolve the category to pre-set when raising from a complaint, so the locked field can show the
 * master's own label rather than a hardcoded string. Returns null while the categories are still
 * loading, or if the row is absent from this environment's master.
 *
 * Resolved id-first then by name, the same order as isComplaintLinkedCategory — the two have to
 * agree on which category this is, or the drawer pre-sets one the complaint field will not mount
 * for.
 */
export const findComplaintDrawerCategoryOption = (
    categories: ProjectCategoryOption[],
    options: DropdownObj[]
): DropdownObj | null => {
    const match =
        categories.find(category =>
            COMPLAINT_LINKED_CATEGORY_IDS.includes(Number(category.category_id))
        ) ?? categories.find(category => isLinkedCategoryName(category.category_name))
    if (!match) return null
    // Prefer the built option so the label matches the dropdown exactly (including any
    // "(Inactive)" suffix useProjectCategoryOptions appends); fall back to the raw master row.
    return (
        options.find(option => option.id === match.category_id) ?? {
            id: match.category_id,
            title: match.category_name,
        }
    )
}
